import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ShoppingCart, User, LogOut, Phone, Home, CreditCard, 
  ChevronRight, ChevronDown, Car, Ship, LayoutGrid, Building2, X
} from 'lucide-react';

export default function Header({
  language,
  setLanguage,
  cart,
  setShowCart,
  isAuthenticated,
  handleLogout,
  setShowAuthModal,
  setShowContactModal,
  t
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartPop, setCartPop] = useState(false);
  const [stockDropdownOpen, setStockDropdownOpen] = useState(false);
  
  const timeoutRef = useRef(null);
  const prevCartLen = useRef(cart.length);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setStockDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setStockDropdownOpen(false);
    }, 200);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (cart.length > prevCartLen.current) {
      setCartPop(true);
      setTimeout(() => setCartPop(false), 400);
    }
    prevCartLen.current = cart.length;
  }, [cart.length]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
  }, [mobileMenuOpen]);

  const isStockActive = ['/stock', '/automobiles', '/bateaux', '/bungalows'].includes(location.pathname);

  const stockItems = [
    { path: '/stock',       icon: LayoutGrid, label: 'Tout le stock' },
    { path: '/automobiles', icon: Car,        label: 'Automobiles' },
    { path: '/bateaux',     icon: Ship,       label: 'Bateaux' },
    { path: '/bungalows',   icon: Building2,  label: 'Bungalows' },
  ];

  const NavLink = ({ path, label }) => (
    <button
      onClick={() => navigate(path)}
      className={`relative font-bold text-[13px] uppercase tracking-[0.15em] transition-colors duration-300 whitespace-nowrap group ${
        location.pathname === path ? 'text-red-500' : 'text-gray-400 hover:text-white'
      }`}
    >
      {label}
      <span className={`absolute -bottom-2 left-0 h-0.5 bg-red-500 rounded-full transition-all duration-300 ${
        location.pathname === path ? 'w-full' : 'w-0 group-hover:w-full'
      }`} />
    </button>
  );

  return (
    <>
      <style>{`
        @keyframes shimmer { 100% { transform: translateX(100%); } }
      `}</style>

      {/* Barre d'annonce */}
      <div className="bg-red-600 text-white text-center py-2 text-[10px] font-black uppercase tracking-[0.2em] relative z-[210]">
        {t.exceptionalOffers}
      </div>

      <header className={`bg-gray-900 text-white sticky top-0 z-[200] transition-all duration-500 ${
        scrolled ? 'shadow-[0_10px_40px_rgba(0,0,0,0.8)] py-3' : 'shadow-lg py-5 md:py-7'
      }`}>
        <div className="max-w-[1600px] mx-auto px-4 md:px-10">
          <div className="flex justify-between items-center">

            {/* LOGO */}
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
              <img
                src="https://res.cloudinary.com/ds9vdtt8l/image/upload/v1772841664/logo_pp_p3oe37.jpg"
                alt="Logo"
                className={`object-cover rounded-xl border border-gray-700 transition-all duration-500 group-hover:border-red-500 ${scrolled ? 'h-9 w-9 md:h-10 md:w-10' : 'h-10 w-10 md:h-12 md:w-12'}`}
              />
              <div className="flex flex-col">
                <h1 className="text-sm md:text-2xl font-black tracking-tighter uppercase italic leading-none">
                  TECHNIC <span className="text-red-500">IMPORT</span>
                </h1>
                <span className="hidden xs:block text-[8px] md:text-[10px] text-gray-500 tracking-[0.3em] uppercase font-bold">Outre-Mer</span>
              </div>
            </div>

            {/* NAV PC */}
            <nav className="hidden lg:flex items-center gap-10">
              <NavLink path="/" label="Accueil" />
              <div className="relative h-full flex items-center" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <button className={`flex items-center gap-2 font-bold text-[13px] uppercase tracking-widest ${isStockActive ? 'text-red-500' : 'text-gray-400 hover:text-white'}`}>
                  {t.ourStock} <ChevronDown size={14} className={stockDropdownOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
                </button>
                <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-60 transition-all ${stockDropdownOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                   <div className="bg-gray-800 border border-gray-700/50 rounded-2xl p-2 shadow-2xl">
                      {stockItems.map((item) => (
                        <button key={item.path} onClick={() => {navigate(item.path); setStockDropdownOpen(false);}} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700 rounded-xl text-[11px] font-black uppercase transition-all">
                          <item.icon size={16} className="text-red-500" /> {item.label}
                        </button>
                      ))}
                   </div>
                </div>
              </div>
              <NavLink path="/financing" label={t.financing} />

              <div className="flex items-center gap-4 border-l border-gray-800 pl-8">
                <div className="flex items-center bg-gray-800/40 border border-gray-700/50 rounded-2xl p-1">
                  
                  {/* LOGIQUE DÉCONNEXION / COMPTE PC */}
                  {isAuthenticated ? (
                    <button 
                      onClick={handleLogout} 
                      className="flex items-center gap-2 px-3 py-2 text-red-400 hover:text-red-300 transition-all group"
                      title="Se déconnecter"
                    >
                      <LogOut size={18} className="group-hover:translate-x-1 transition-transform" />
                      <span className="text-[10px] font-black uppercase hidden xl:block">Déconnexion</span>
                    </button>
                  ) : (
                    <button 
                      onClick={() => setShowAuthModal(true)} 
                      className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-all"
                    >
                      <User size={18} />
                      <span className="text-[10px] font-black uppercase hidden xl:block">Mon Compte</span>
                    </button>
                  )}

                  <div className="w-px h-4 bg-gray-700 mx-1" />
                  
                  <button onClick={() => setShowCart(true)} className="relative p-2 text-gray-400 hover:text-white transition-all group">
                    <ShoppingCart size={18} className="group-hover:-rotate-12" />
                    {cart.length > 0 && <span className="absolute top-0 right-0 bg-red-600 text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-black animate-pulse">{cart.length}</span>}
                  </button>
                </div>

                <button onClick={() => setShowContactModal(true)} className="bg-red-600 px-6 py-3 rounded-xl font-black text-[11px] uppercase tracking-widest hover:scale-105 transition-all flex items-center gap-2 relative overflow-hidden group text-white">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                  <Phone size={14} /> {t.callUs}
                </button>
              </div>
            </nav>

            {/* ZONE MOBILE QUICK ACCESS */}
            <div className="lg:hidden flex items-center gap-3">
              <button onClick={() => setShowCart(true)} className="relative p-2.5 bg-gray-800/50 rounded-xl text-gray-400 border border-gray-700/50">
                <ShoppingCart size={22} />
                {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-gray-900 text-white">{cart.length}</span>}
              </button>
              <button 
                onClick={() => setMobileMenuOpen(true)} 
                className="flex items-center gap-2 bg-white text-gray-900 px-3 py-2 rounded-xl active:scale-95 shadow-lg"
              >
                <div className="flex flex-col gap-1">
                  <div className="w-4 h-0.5 bg-gray-900 rounded-full" />
                  <div className="w-3 h-0.5 bg-red-600 rounded-full ml-auto" />
                </div>
                <span className="text-[11px] font-black uppercase tracking-tighter">Menu</span>
              </button>
            </div>
          </div>
        </div>

        {/* DRAWER MOBILE */}
        <div className={`fixed inset-0 z-[400] lg:hidden transition-all duration-500 ${mobileMenuOpen ? 'visible' : 'invisible'}`}>
          <div className={`absolute inset-0 bg-black/95 backdrop-blur-md transition-opacity duration-500 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setMobileMenuOpen(false)} />
          
          <div className={`absolute top-0 right-0 h-full w-[85%] bg-gray-900 p-6 pt-12 shadow-2xl transition-transform duration-500 transform ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            
            <div className="flex justify-between items-center pb-6 border-b border-gray-800 mb-8 text-white">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 bg-red-600 rounded-full" />
                <span className="text-xl font-black italic uppercase tracking-tighter">Technic <span className="text-red-500 text-sm font-bold">Menu</span></span>
              </div>
              <button 
                onClick={() => setMobileMenuOpen(false)} 
                className="p-3 bg-gray-800 rounded-2xl text-white border border-gray-700 shadow-xl active:bg-red-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-250px)] text-white">
              <button onClick={() => {navigate('/'); setMobileMenuOpen(false);}} className="w-full flex items-center justify-between p-4 bg-gray-800/30 hover:bg-gray-800 rounded-2xl transition-all">
                <span className="font-bold flex items-center gap-3 uppercase text-[12px] tracking-widest"><Home size={18} className="text-red-500"/> Accueil</span>
                <ChevronRight size={16} className="text-gray-600" />
              </button>

              <div className="py-4 space-y-2">
                <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest mb-2 px-2">Catalogues</p>
                <div className="grid grid-cols-2 gap-2">
                  {stockItems.map(item => (
                    <button key={item.path} onClick={() => {navigate(item.path); setMobileMenuOpen(false);}} className="flex flex-col items-center gap-3 p-5 bg-gray-800/50 rounded-2xl border border-gray-700/30 active:border-red-500/50">
                      <item.icon className="text-red-500" size={26} />
                      <span className="text-[10px] font-black uppercase text-center tracking-tighter">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={() => {navigate('/financing'); setMobileMenuOpen(false);}} className="w-full flex items-center justify-between p-4 bg-gray-800/30 hover:bg-gray-800 rounded-2xl transition-all">
                <span className="font-bold flex items-center gap-3 uppercase text-[12px] tracking-widest"><CreditCard size={18} className="text-red-500"/> Financement</span>
                <ChevronRight size={16} className="text-gray-600" />
              </button>
            </div>

            {/* ACTIONS FIXES BAS (LOGIQUE AUTH MOBILE) */}
            <div className="absolute bottom-8 left-6 right-6 space-y-3 bg-gray-900 pt-4 text-white">
              {isAuthenticated ? (
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }} 
                  className="w-full py-4 border border-red-900/30 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 bg-red-950/20 text-red-500"
                >
                  <LogOut size={16} /> Déconnexion
                </button>
              ) : (
                <button 
                  onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }} 
                  className="w-full py-4 border border-gray-700 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 bg-gray-800/50"
                >
                  <User size={16} /> Mon Compte
                </button>
              )}

              <button 
                onClick={() => { setShowContactModal(true); setMobileMenuOpen(false); }} 
                className="w-full py-4 bg-red-600 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-red-600/30 active:scale-95 transition-transform text-white"
              >
                <Phone size={16} fill="currentColor" /> {t.callUs}
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
